import { useMemo, useState } from "react";
import { Button, Input } from "reactstrap";

import AppDrawer from "../AppDrawer";
import {
  EmailBuilderPages,
  EmailTemplateTypes,
  EmailVariables,
  SavedBulkEmailOperations,
} from "./EmailBuilderTypes";
import BuilderHeader from "./BuilderHeader";
import SelectionPage from "./SelectionPage";
import EmailVariablesBuilder from "./EmailVariablesBuilder";
import RequiredTitle from "../../../elements/RequiredTitle";
import RichTextEditorV2 from "../../CRM/RichTextEditorV2";
import useBulkEmailCRUD from "../../../api/api-hooks/bulkEmail/useBulkEmailCRUD";
import AppSpinner from "../../../elements/AppSpinner";

interface Props {
  open: boolean;
  onClose: () => void;
  onExited: () => void;
  existing?: SavedBulkEmailOperations;
}

const empty: [] = [];

const EmailBuilderDrawer = ({ open, onClose, onExited, existing }: Props) => {
  const [error, setError] = useState("");
  const [expandAll, setExpandAll] = useState(false);
  const [page, setPage] = useState<EmailBuilderPages>(
    existing?.Type || "Selection"
  );
  const [templateType, setTemplateType] = useState<EmailTemplateTypes | "">(
    existing?.Type || ""
  );
  const [templateName, setTemplateName] = useState(existing?.Name || "");
  const [fromName, setFromName] = useState(existing?.From?.name || "");
  const [fromEmail, setFromEmail] = useState(existing?.From?.email || "");
  const [subject, setSubject] = useState(existing?.Subject || "");
  const [globalAppID, setGlobalAppID] = useState(existing?.GlobalAppID || "");
  const [templateDescription, setTemplateDescription] = useState(
    existing?.Description || ""
  );
  const [customTemplate, setCustomTemplate] = useState(
    existing?.Type === "Custom" ? existing?.Content || "" : ""
  );
  const [variables, setVariables] = useState<EmailVariables[]>(
    existing?.Variables || []
  );
  const [sendgridID, setSendgridID] = useState(
    existing?.Type === "Dynamic" ? existing?.TemplateID || "" : ""
  );
  const [unsubscribeGroupID, setUnsubscribeGroupID] = useState(
    existing?.Type === "Dynamic" ? existing?.UnsubscribeGroupID || "" : ""
  );
  const { bulkEmailCRUD, crudLoading } = useBulkEmailCRUD();

  // For Custom Templates, grab all the variables from the rich text editor.
  const customVariables = useMemo(() => {
    const variableRegex = /{{(.*?)}}/g;
    const matches = customTemplate.match(variableRegex);

    if (!matches) return empty;

    const variableNames = matches.flatMap((match) => {
      const result = match.replace(/[{}]/g, "").trim();

      return result ? [result] : [];
    });

    return variableNames;
  }, [customTemplate]);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      onExited={onExited}
      backdrop="static"
      size="md"
      headerTitle="Email Builder"
      headerDescription="Creating an email template for your cloud."
      error={
        error ? { message: error, onClear: () => setError("") } : undefined
      }
      headerOptions={
        <>
          {page !== "Selection" ? (
            <Button size="sm" onClick={() => setPage("Selection")}>
              Go Back
            </Button>
          ) : (
            <Button
              size="sm"
              style={{ width: 100 }}
              onClick={() => setPage(templateType || "Selection")}
              disabled={
                !templateType || !templateName || !fromName || !fromEmail
              }
              color="primary"
            >
              Next
            </Button>
          )}
          {(page === "Dynamic" || page === "Custom") && (
            <Button
              size="sm"
              style={{ width: 100 }}
              onClick={onSave}
              disabled={
                !templateType ||
                !templateName ||
                (templateType === "Dynamic" && !sendgridID) ||
                (templateType === "Dynamic" && !unsubscribeGroupID) ||
                (templateType === "Custom" && !customTemplate) ||
                crudLoading
              }
              color="primary"
            >
              {crudLoading ? <AppSpinner color="white" /> : "Save"}
            </Button>
          )}
        </>
      }
      body={
        <>
          {page === "Selection" && (
            <SelectionPage
              templateType={templateType}
              setTemplateType={setTemplateType}
              templateName={templateName}
              setTemplateName={setTemplateName}
              templateDescription={templateDescription}
              setTemplateDescription={setTemplateDescription}
              fromName={fromName}
              setFromName={setFromName}
              fromEmail={fromEmail}
              setFromEmail={setFromEmail}
              subject={subject}
              setSubject={setSubject}
              globalAppID={globalAppID}
              setGlobalAppID={setGlobalAppID}
              setPage={setPage}
            />
          )}
          {page === "Dynamic" && (
            <>
              <RequiredTitle
                titleCSS="mt-0 text-primary"
                title="SendGrid Template ID"
                size="md"
              />
              <Input
                bsSize="sm"
                placeholder="Enter template ID from SendGrid."
                value={sendgridID}
                onChange={(e) => setSendgridID(e.target.value.trim())}
                spellCheck={false}
              />
              <RequiredTitle
                titleCSS="text-primary"
                title="Enter Unsubscribe Group ID from SendGrid"
                size="md"
                className="mt-3"
              />
              <Input
                bsSize="sm"
                placeholder="Enter template ID from SendGrid."
                type="number"
                value={unsubscribeGroupID}
                onChange={(e) => setUnsubscribeGroupID(e.target.value)}
                spellCheck={false}
              />
              <hr className="bg-superlight" />
              <BuilderHeader
                title="Dynamic Template Variables"
                variables={variables}
                setVariables={setVariables}
                expandAll={expandAll}
                setExpandAll={setExpandAll}
              />
              <EmailVariablesBuilder
                variables={variables}
                setVariables={setVariables}
                expandAll={expandAll}
              />
            </>
          )}
          {page === "Custom" && (
            <>
              <RequiredTitle title="Custom Template" size="lg" />
              {/* @ts-ignore */}
              <RichTextEditorV2
                placeholder="Your custom email template. For variables use: '{{ variable_name }}'"
                isEditing={true}
                value={customTemplate}
                onChange={(newMarkup: string) => setCustomTemplate(newMarkup)}
                css="mb-4"
                useBrie={false}
              />
              <BuilderHeader
                title="Variables"
                variables={variables}
                setVariables={setVariables}
                expandAll={expandAll}
                setExpandAll={setExpandAll}
              />
              <EmailVariablesBuilder
                variables={variables}
                setVariables={setVariables}
                customVariables={customVariables}
                expandAll={expandAll}
              />
            </>
          )}
        </>
      }
    />
  );

  function handleClose() {
    onClose();
    setPage("Selection");
    setTemplateType("");
    setTemplateName("");
    setVariables([]);
    setSendgridID("");
    setFromName("");
    setFromEmail("");
    setTemplateDescription("");
    setCustomTemplate("");
    setGlobalAppID("");
  }

  function onSave() {
    if (!templateName || !templateType) return;

    const payload: any = {
      Name: templateName,
      Description: templateDescription,
      Variables: variables,
      Type: templateType,
      Subject: subject,
      From: {
        name: fromName,
        email: fromEmail,
      },
    };

    if (templateType === "Custom") {
      payload.Content = customTemplate;
    }

    if (templateType === "Dynamic") {
      payload.TemplateID = sendgridID;
      payload.UnsubscribeGroupID = Number(unsubscribeGroupID);
    }

    if (globalAppID) {
      payload.GlobalAppID = globalAppID;
    }

    bulkEmailCRUD({
      actionID: existing?.id,
      payload: payload,
      action: existing?.id ? "Update" : "Create",
      onError: (msg) => setError(msg),
      onSuccess: handleClose,
    });
  }
};

export default EmailBuilderDrawer;
